/**
 * Toasts.
 *
 * Used for the result of an action the operator just took. Anything they need
 * to read carefully, such as why a send was blocked, belongs on the page in an
 * ErrorState, not in something that disappears.
 *
 * Salesforge's shape: top centre, 20 from the top, a solid fill per tone with
 * white text, a close that is always there, eight seconds on screen, at most
 * three in view with the newest on top. Hovering or focusing the stack pauses
 * every timer in it, so a toast cannot vanish while somebody is reading it or
 * reaching for its action.
 *
 * The success and warning fills are the 700 steps, not Salesforge's 600s,
 * because white on those measures 3.91 and 3.49 and fails. The fills do not
 * change with the theme: a solid toast already stands off a dark ground.
 */
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { errorMessage } from '../lib/errors'
import { IconAlert, IconAlertCircle, IconCheckCircle, IconClose, IconInfo } from './icons'

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger'

/** Where a toast sits. The new-version notice is the one that sits bottom right. */
export type ToastPosition = 'top' | 'bottom-right'

export interface ToastOptions {
  title: string
  /** A sentence, or a list of short points drawn as bullets. */
  description?: ReactNode | readonly string[]
  tone?: ToastTone
  /**
   * Milliseconds on screen, 8000 by default. Pass 0 or `Infinity` to require
   * a dismiss. `Infinity` also moves the toast bottom right, which is where
   * the new-version notice lives.
   */
  duration?: number
  action?: { label: string; onClick: () => void }
  position?: ToastPosition
}

interface ToastRecord extends ToastOptions {
  id: number
}

export interface ToastApi {
  toast: (options: ToastOptions) => number
  success: (title: string, description?: ReactNode) => number
  error: (title: string, error?: unknown) => number
  dismiss: (id: number) => void
}

/** How long a toast stays, measured. */
export const TOAST_DURATION_MS = 8000

/** How many show at once. A fourth waits under the fold of the stack. */
export const TOAST_MAX_VISIBLE = 3

/**
 * The fills, as values for the contrast test and as the literal classes
 * Tailwind has to be able to read whole. The test checks the two agree.
 */
export const TOAST_FILL_HEX: Record<ToastTone, string> = {
  success: '#067647',
  warning: '#B54708',
  danger: '#D92D20',
  neutral: '#1D2939',
}

const TOAST_FILL: Record<ToastTone, string> = {
  success: 'bg-[#067647]',
  warning: 'bg-[#B54708]',
  danger: 'bg-[#D92D20]',
  neutral: 'bg-[#1D2939]',
}

const TOAST_ICON: Record<ToastTone, (props: { className?: string }) => ReactNode> = {
  success: IconCheckCircle,
  warning: IconAlert,
  danger: IconAlertCircle,
  neutral: IconInfo,
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside ToastProvider')
  return api
}

// ---------------------------------------------------------------------------
// The rules, as plain functions and one small class
// ---------------------------------------------------------------------------

/** Whether a duration means "until dismissed". */
export function isSticky(duration: number): boolean {
  return !Number.isFinite(duration) || duration <= 0
}

export function resolveDuration(options: Pick<ToastOptions, 'duration'>): number {
  return options.duration ?? TOAST_DURATION_MS
}

export function resolvePosition(options: Pick<ToastOptions, 'duration' | 'position'>): ToastPosition {
  if (options.position) return options.position
  return options.duration === Infinity ? 'bottom-right' : 'top'
}

/** The toasts to draw at one position: newest first, at most three. */
export function visibleToasts<T extends { id: number }>(list: readonly T[], max = TOAST_MAX_VISIBLE): T[] {
  return [...list].sort((a, b) => b.id - a.id).slice(0, max)
}

/**
 * One toast's countdown, which can stop and carry on where it left off.
 *
 * Pausing keeps what was left rather than restarting, so somebody who hovers a
 * toast twice does not keep it alive forever, and somebody who reads it for
 * six seconds still gets the two that were left when they move away.
 */
export class ToastClock {
  private remaining: number
  private startedAt = 0
  private handle: ReturnType<typeof setTimeout> | null = null
  private done = false

  constructor(
    duration: number,
    private readonly onExpire: () => void,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.remaining = duration
  }

  /** Starts or carries on. A sticky duration never starts. */
  resume(): void {
    if (this.done || this.handle !== null || isSticky(this.remaining)) return
    this.startedAt = this.now()
    this.handle = setTimeout(() => {
      this.handle = null
      this.done = true
      this.onExpire()
    }, this.remaining)
  }

  pause(): void {
    if (this.handle === null) return
    clearTimeout(this.handle)
    this.handle = null
    this.remaining = Math.max(1, this.remaining - (this.now() - this.startedAt))
  }

  clear(): void {
    if (this.handle !== null) clearTimeout(this.handle)
    this.handle = null
    this.done = true
  }

  /** For tests: what is left, in milliseconds. */
  left(): number {
    return this.handle === null ? this.remaining : Math.max(0, this.remaining - (this.now() - this.startedAt))
  }
}

// ---------------------------------------------------------------------------
// The provider
// ---------------------------------------------------------------------------

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const nextId = useRef(1)
  const clocks = useRef(new Map<number, ToastClock>())
  const paused = useRef(false)

  const dismiss = useCallback((id: number) => {
    clocks.current.get(id)?.clear()
    clocks.current.delete(id)
    setToasts((current) => current.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current
      nextId.current += 1
      const clock = new ToastClock(resolveDuration(options), () => dismiss(id))
      clocks.current.set(id, clock)
      if (!paused.current) clock.resume()
      setToasts((current) => [...current, { ...options, id }])
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const all = clocks.current
    return () => {
      for (const clock of all.values()) clock.clear()
      all.clear()
    }
  }, [])

  const setPaused = useCallback((next: boolean) => {
    if (paused.current === next) return
    paused.current = next
    for (const clock of clocks.current.values()) {
      if (next) clock.pause()
      else clock.resume()
    }
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, description, tone: 'success' }),
      error: (title, error) =>
        toast({
          title,
          description: error === undefined ? undefined : errorMessage(error),
          tone: 'danger',
        }),
    }),
    [toast, dismiss],
  )

  const top = visibleToasts(toasts.filter((item) => resolvePosition(item) === 'top'))
  const corner = visibleToasts(toasts.filter((item) => resolvePosition(item) === 'bottom-right'))

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <>
          <ToastStack position="top" toasts={top} onDismiss={dismiss} onPause={setPaused} />
          <ToastStack position="bottom-right" toasts={corner} onDismiss={dismiss} onPause={setPaused} />
        </>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// The painted half
// ---------------------------------------------------------------------------

const STACK_POSITION: Record<ToastPosition, string> = {
  top: 'top-5 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
}

export interface ToastStackProps {
  position: ToastPosition
  /** Already in drawing order, newest first. */
  toasts: readonly ToastRecord[]
  onDismiss: (id: number) => void
  /** Pointer or focus inside the stack, so the timers stop. */
  onPause?: (paused: boolean) => void
}

/** One stack of toasts at one position, rendered on its own so it can be asserted on. */
export function ToastStack({ position, toasts, onDismiss, onPause }: ToastStackProps) {
  return (
    <div
      data-toast-stack={position}
      className={cn(
        'pointer-events-none fixed z-toast flex w-[27.5rem] max-w-[calc(100vw-32px)] flex-col gap-2',
        STACK_POSITION[position],
      )}
      role="region"
      aria-label="Notifications"
      onMouseEnter={() => onPause?.(true)}
      onMouseLeave={() => onPause?.(false)}
      onFocus={() => onPause?.(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onPause?.(false)
      }}
    >
      {toasts.map((item) => (
        <ToastBody key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastBody({ item, onDismiss }: { item: ToastRecord; onDismiss: (id: number) => void }) {
  const tone = item.tone ?? 'neutral'
  const Glyph = TOAST_ICON[tone]
  const { description } = item
  return (
    <div
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto relative flex animate-toast-in items-start gap-4 rounded-lg py-4 pl-4 pr-10 text-white shadow-xs',
        TOAST_FILL[tone],
      )}
    >
      <Glyph className="mt-px !h-5 !w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">{item.title}</p>
        {Array.isArray(description) ? (
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-base">
            {(description as readonly string[]).map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        ) : description ? (
          <div className="mt-1 text-base">{description as ReactNode}</div>
        ) : null}
        {item.action ? (
          <button
            type="button"
            onClick={() => {
              item.action?.onClick()
              onDismiss(item.id)
            }}
            className="mt-2 text-base font-semibold text-white underline underline-offset-2"
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss"
        className="absolute right-3 top-3 rounded-md p-1 text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        <IconClose className="!h-5 !w-5" />
      </button>
    </div>
  )
}
